import React, { useEffect, useState } from 'react';
import { BOAPI } from "../service/UsersService";

const emptyOption = () => ({ text: '', isCorrect: false });
const emptyQuestion = () => ({ text: '', options: [emptyOption(), emptyOption(), emptyOption(), emptyOption()] });
const emptyForm = () => ({
  storyId: null,
  name: '',
  imageUrl: '',
  description: '',
  startDate: '',
  endDate: '',
  title: '',
  questions: [emptyQuestion()],
});

// מקבץ את השורות השטוחות שמחזיר spBOGetQuizDetails (questionId/questionText/
// questionIndex/optionId/optionText/isCorrect/optionIndex) לרשימת שאלות
// עם 4 אפשרויות כל אחת — אותה לוגיקת קיבוץ כמו ב-StoryQuizScreen.tsx באפליקציה.
const groupQuestionRows = (rows) => {
  const map = new Map();
  rows
    .slice()
    .sort((a, b) => (a.questionIndex ?? 0) - (b.questionIndex ?? 0))
    .forEach((row) => {
      if (!map.has(row.questionId)) {
        map.set(row.questionId, { text: row.questionText, options: [] });
      }
      map.get(row.questionId).options.push({
        text: row.optionText,
        isCorrect: !!row.isCorrect,
        _index: row.optionIndex,
      });
    });
  return Array.from(map.values()).map((q) => ({
    text: q.text,
    options: q.options.sort((a, b) => a._index - b._index).map(({ text, isCorrect }) => ({ text, isCorrect })),
  }));
};

const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const QuizzesTab = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadQuizzes();
  }, []);

  const loadQuizzes = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await BOAPI.fetchQuizzes();

      if (Array.isArray(result)) {
        const [statusObj, data] = result;
        if (statusObj[0]?.code === 0 && Array.isArray(data)) {
          setQuizzes(data);
        } else {
          setError(statusObj[0]?.msg || 'שגיאה בטעינת המבחנים');
        }
      }
    } catch (err) {
      setError(err.message || 'שגיאה בטעינת המבחנים');
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setFormData(emptyForm());
    setShowForm(true);
    setError(null);
  };

  const handleEdit = async (item) => {
    try {
      setError(null);
      const result = await BOAPI.fetchQuizDetails(item.storyId);
      if (!Array.isArray(result)) return;
      const [statusObj, storyRows, questionRows] = result;
      if (statusObj[0]?.code !== 0) {
        setError(statusObj[0]?.msg || 'שגיאה בטעינת המבחן');
        return;
      }
      const story = storyRows?.[0];
      setFormData({
        storyId: item.storyId,
        name: story?.name ?? item.name,
        imageUrl: story?.imageUrl ?? item.imageUrl ?? '',
        description: story?.description ?? item.description ?? '',
        startDate: formatDateForInput(story?.startDate ?? item.startDate),
        endDate: formatDateForInput(story?.endDate ?? item.endDate),
        title: story?.title ?? item.title,
        questions: groupQuestionRows(questionRows || []),
      });
      setShowForm(true);
    } catch (err) {
      setError(err.message || 'שגיאה בטעינת המבחן');
      console.error("Error:", err);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setFormData(emptyForm());
  };

  const handleFieldChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleQuestionTextChange = (qIdx, value) => {
    const questions = formData.questions.map((q, i) => (i === qIdx ? { ...q, text: value } : q));
    setFormData({ ...formData, questions });
  };

  const handleOptionTextChange = (qIdx, oIdx, value) => {
    const questions = formData.questions.map((q, i) => {
      if (i !== qIdx) return q;
      const options = q.options.map((o, j) => (j === oIdx ? { ...o, text: value } : o));
      return { ...q, options };
    });
    setFormData({ ...formData, questions });
  };

  const handleCorrectOptionChange = (qIdx, oIdx) => {
    const questions = formData.questions.map((q, i) => {
      if (i !== qIdx) return q;
      const options = q.options.map((o, j) => ({ ...o, isCorrect: j === oIdx }));
      return { ...q, options };
    });
    setFormData({ ...formData, questions });
  };

  const handleAddQuestion = () => {
    setFormData({ ...formData, questions: [...formData.questions, emptyQuestion()] });
  };

  const handleRemoveQuestion = (qIdx) => {
    if (formData.questions.length <= 1) return; // לפחות שאלה אחת
    setFormData({ ...formData, questions: formData.questions.filter((_, i) => i !== qIdx) });
  };

  const validateForm = () => {
    if (!formData.name.trim()) return 'יש להזין שם לסטורי';
    if (!formData.title.trim()) return 'יש להזין כותרת למבחן';
    if (!formData.startDate || !formData.endDate) return 'יש להזין תאריך התחלה וסיום';
    if (new Date(formData.endDate) <= new Date(formData.startDate)) return 'תאריך הסיום צריך להיות אחרי תאריך ההתחלה';
    for (let i = 0; i < formData.questions.length; i++) {
      const q = formData.questions[i];
      if (!q.text.trim()) return `יש להזין טקסט לשאלה ${i + 1}`;
      if (q.options.some((o) => !o.text.trim())) return `יש למלא את כל 4 האפשרויות בשאלה ${i + 1}`;
      if (!q.options.some((o) => o.isCorrect)) return `יש לסמן תשובה נכונה אחת בשאלה ${i + 1}`;
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const params = {
        storyId: formData.storyId,
        name: formData.name.trim(),
        imageUrl: formData.imageUrl.trim(),
        description: formData.description.trim(),
        startDate: formData.startDate,
        endDate: formData.endDate,
        title: formData.title.trim(),
        questions: formData.questions.map((q) => ({
          text: q.text.trim(),
          options: q.options.map((o) => ({ text: o.text.trim(), isCorrect: !!o.isCorrect })),
        })),
      };

      const resp = await BOAPI.saveQuiz(params);

      if (Array.isArray(resp)) {
        const [statusObj] = resp;
        if (statusObj[0]?.code === 0) {
          setShowForm(false);
          setFormData(emptyForm());
          await loadQuizzes();
        } else {
          setError(statusObj[0]?.msg || 'שגיאה בשמירת המבחן');
        }
      }
    } catch (err) {
      setError(err.message || 'שגיאה בשמירת המבחן');
      console.error("Error:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`למחוק את המבחן "${item.title}"?`)) return;
    try {
      const resp = await BOAPI.deleteQuiz(item.storyId);
      if (Array.isArray(resp)) {
        const [statusObj] = resp;
        if (statusObj[0]?.code === 0) {
          setQuizzes(quizzes.filter((q) => q.storyId !== item.storyId));
        } else {
          setError(statusObj[0]?.msg || 'שגיאה במחיקת המבחן');
        }
      }
    } catch (err) {
      setError(err.message || 'שגיאה במחיקת המבחן');
      console.error("Error:", err);
    }
  };

  if (loading) {
    return <div style={styles.center}>טוען מבחנים...</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.headerSection}>
        <h2 style={styles.title}>בחן את עצמך</h2>
        {!showForm && (
          <button onClick={handleAddNew} style={styles.addButton}>
            ➕ הוסף מבחן חדש
          </button>
        )}
      </div>

      {error && (
        <div style={styles.error}>
          ❌ {error}
          <button onClick={() => setError(null)} style={styles.closeErrorButton}>✕</button>
        </div>
      )}

      {showForm && (
        <div style={styles.formCard}>
          <h3 style={styles.formTitle}>{formData.storyId ? 'עריכת מבחן' : 'מבחן חדש'}</h3>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>שם הסטורי *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                style={styles.input}
                maxLength={32}
                placeholder="בחן את עצמך"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>כותרת המבחן *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                style={styles.input}
                placeholder="ידע כללי"
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>קישור לתמונה</label>
            <input
              type="text"
              value={formData.imageUrl}
              onChange={(e) => handleFieldChange('imageUrl', e.target.value)}
              style={styles.input}
              placeholder="https://..."
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>תיאור / כיתוב</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              style={styles.textarea}
              rows={2}
              placeholder="כמה אתם יודעים? בואו לבדוק!"
            />
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>תאריך התחלה *</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleFieldChange('startDate', e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>תאריך סיום *</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => handleFieldChange('endDate', e.target.value)}
                style={styles.input}
              />
            </div>
          </div>

          <h4 style={styles.questionsTitle}>שאלות</h4>
          {formData.questions.map((q, qIdx) => (
            <div key={qIdx} style={styles.questionCard}>
              <div style={styles.questionHeader}>
                <span style={styles.questionNumber}>שאלה {qIdx + 1}</span>
                <button
                  onClick={() => handleRemoveQuestion(qIdx)}
                  style={styles.removeQuestionBtn}
                  disabled={formData.questions.length <= 1}
                  title="הסר שאלה"
                >
                  🗑️ הסר שאלה
                </button>
              </div>

              <input
                type="text"
                value={q.text}
                onChange={(e) => handleQuestionTextChange(qIdx, e.target.value)}
                style={styles.input}
                placeholder="טקסט השאלה"
              />

              <div style={styles.optionsList}>
                {q.options.map((opt, oIdx) => (
                  <div key={oIdx} style={styles.optionRow}>
                    <label style={styles.radioLabel}>
                      <input
                        type="radio"
                        name={`correct-${qIdx}`}
                        checked={opt.isCorrect}
                        onChange={() => handleCorrectOptionChange(qIdx, oIdx)}
                      />
                      נכונה
                    </label>
                    <input
                      type="text"
                      value={opt.text}
                      onChange={(e) => handleOptionTextChange(qIdx, oIdx, e.target.value)}
                      style={styles.optionInput}
                      placeholder={`אפשרות ${oIdx + 1}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <button onClick={handleAddQuestion} style={styles.addQuestionBtn}>
            ➕ הוסף שאלה
          </button>

          <div style={styles.formButtons}>
            <button onClick={handleSave} style={styles.saveButton} disabled={saving}>
              {saving ? 'שומר...' : '💾 שמירה'}
            </button>
            <button onClick={handleCancelForm} style={styles.cancelButton} disabled={saving}>
              ✕ בטל
            </button>
          </div>
        </div>
      )}

      {quizzes.length === 0 ? (
        <div style={styles.noData}>אין מבחנים להצגה</div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.headerRow}>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>כותרת</th>
                <th style={styles.th}>שם הסטורי</th>
                <th style={styles.th}>שאלות</th>
                <th style={styles.th}>סטטוס</th>
                <th style={styles.th}>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {quizzes
                .sort((a, b) => b.storyId - a.storyId)
                .map((item) => (
                  <tr key={item.storyId} style={styles.row}>
                    <td style={styles.td}><div style={styles.idText}>{item.storyId}</div></td>
                    <td style={styles.td}><div style={styles.valueText}>{item.title}</div></td>
                    <td style={styles.td}>{item.name}</td>
                    <td style={styles.td}>{item.questionCount}</td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.statusBadge,
                          backgroundColor: item.isActive ? '#e8f5e9' : '#ffebee',
                          color: item.isActive ? '#2e7d32' : '#c62828',
                        }}
                      >
                        {item.isActive ? '✓ פעיל' : 'לא פעיל'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionButtons}>
                        <button onClick={() => handleEdit(item)} style={styles.editButton}>✏️ ערוך</button>
                        <button onClick={() => handleDelete(item)} style={styles.cancelButton}>🗑️ מחק</button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { padding: '20px', direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerSection: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' },
  title: { margin: 0, color: '#1a1f36', fontSize: '24px', fontWeight: 'bold' },
  addButton: {
    padding: '10px 20px', backgroundColor: '#4caf50', color: 'white', border: 'none',
    borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold',
  },
  formCard: { backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '20px' },
  formTitle: { marginTop: 0, marginBottom: '20px', color: '#333', fontSize: '18px' },
  formGroup: { marginBottom: '15px' },
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '15px' },
  label: { display: 'block', marginBottom: '5px', color: '#333', fontWeight: 'bold', fontSize: '14px' },
  input: {
    width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd',
    borderRadius: '4px', boxSizing: 'border-box',
  },
  textarea: {
    width: '100%', padding: '10px', fontFamily: 'Arial, sans-serif', fontSize: '14px',
    border: '1px solid #ddd', borderRadius: '4px', direction: 'rtl', boxSizing: 'border-box', resize: 'none',
  },
  formButtons: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' },

  questionsTitle: { margin: '20px 0 10px', color: '#333', fontSize: '16px' },
  questionCard: {
    backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px',
    padding: '15px', marginBottom: '12px',
  },
  questionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  questionNumber: { fontWeight: 'bold', color: '#1f6feb', fontSize: '14px' },
  removeQuestionBtn: {
    padding: '4px 10px', backgroundColor: '#fff1f1', color: '#d93838', border: '1px solid #fbc4c4',
    borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
  },
  optionsList: { display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' },
  optionRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  optionInput: {
    flex: 1, padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd',
    borderRadius: '4px', boxSizing: 'border-box',
  },
  radioLabel: {
    display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#2e7d32',
    fontWeight: 'bold', whiteSpace: 'nowrap',
  },
  addQuestionBtn: {
    padding: '8px 16px', backgroundColor: '#eef1f8', color: '#1f6feb', border: '1px dashed #1f6feb',
    borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', marginBottom: '10px',
  },

  tableWrapper: { overflowX: 'auto', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
  headerRow: { backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' },
  th: { padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#333', fontSize: '14px', borderBottom: '2px solid #ddd' },
  row: { borderBottom: '1px solid #eee' },
  td: { padding: '15px', color: '#666', fontSize: '14px', textAlign: 'right', verticalAlign: 'top' },
  idText: { fontWeight: 'bold', color: '#999' },
  valueText: { lineHeight: '1.6', color: '#333', fontWeight: '500' },
  statusBadge: { display: 'inline-block', padding: '6px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' },
  editButton: {
    padding: '8px 16px', backgroundColor: '#1f6feb', color: 'white', border: 'none',
    borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold',
  },
  saveButton: {
    padding: '8px 14px', backgroundColor: '#2e7d32', color: 'white', border: 'none',
    borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold',
  },
  cancelButton: {
    padding: '8px 14px', backgroundColor: '#c62828', color: 'white', border: 'none',
    borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold',
  },
  actionButtons: { display: 'flex', gap: '8px', justifyContent: 'flex-end' },
  center: { textAlign: 'center', padding: '40px', color: '#999' },
  error: {
    padding: '15px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px',
    marginBottom: '20px', fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  closeErrorButton: { backgroundColor: 'transparent', color: '#c62828', border: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' },
  noData: { padding: '40px', textAlign: 'center', color: '#999', backgroundColor: '#f5f5f5', borderRadius: '4px' },
};

export default QuizzesTab;
